import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Network, FileText, Pill, Shield, Building, ArrowLeft,
  RefreshCw, CheckCircle, XCircle, AlertCircle, Send, Database, Brain, Sparkles
} from "lucide-react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function IntegrationsDashboard() {
  const [activeTab, setActiveTab] = useState("fhir");
  const [loading, setLoading] = useState(false);
  
  // FHIR state
  const [fhirConnections, setFhirConnections] = useState([]);
  const [adtMessages, setAdtMessages] = useState([]);
  const [supportedResources, setSupportedResources] = useState([]);
  
  // E-Prescribe state
  const [prescriptions, setPrescriptions] = useState([]);
  const [formulary, setFormulary] = useState({});
  const [pharmacies, setPharmacies] = useState([]);
  const [prescriptionForm, setPrescriptionForm] = useState({
    patientId: "",
    patientName: "",
    medication: "morphine_sulfate",
    strength: "30mg",
    quantity: 60,
    daysSupply: 30,
    sig: "Take 30mg by mouth every 4 hours as needed for pain",
    pharmacyId: "pharm_003"
  });
  
  // Insurance state
  const [payers, setPayers] = useState({});
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [benefitResult, setBenefitResult] = useState(null);
  const [patientInsurance, setPatientInsurance] = useState({
    memberId: "",
    payerCode: "medicare",
    patientName: "",
    patientDob: ""
  });

  useEffect(() => {
    loadFhirData();
    loadPrescribingData();
    loadInsuranceData();
  }, []);

  const loadFhirData = async () => {
    try {
      const [connRes, adtRes, resourceRes] = await Promise.all([
        fetch(`${API_URL}/api/fhir/connections`),
        fetch(`${API_URL}/api/fhir/adt/messages?limit=10`),
        fetch(`${API_URL}/api/fhir/resources/supported`)
      ]);
      
      const connections = await connRes.json();
      const adts = await adtRes.json();
      const resources = await resourceRes.json();
      
      setFhirConnections(connections.connections || []);
      setAdtMessages(adts.messages || []);
      setSupportedResources(resources.resources || []);
    } catch (err) {
      console.error("Failed to load FHIR data:", err);
    }
  };

  const loadPrescribingData = async () => {
    try {
      const [formRes, pharmRes] = await Promise.all([
        fetch(`${API_URL}/api/eprescribe/formulary`),
        fetch(`${API_URL}/api/eprescribe/pharmacies`)
      ]);
      
      const form = await formRes.json();
      const pharm = await pharmRes.json();
      
      setFormulary(form.medications || {});
      setPharmacies(pharm.pharmacies || []);
    } catch (err) {
      console.error("Failed to load prescribing data:", err);
    }
  };

  const loadInsuranceData = async () => {
    try {
      const payersRes = await fetch(`${API_URL}/api/insurance/payers`);
      const data = await payersRes.json();
      setPayers(data.payers || {});
    } catch (err) {
      console.error("Failed to load insurance data:", err);
    }
  };

  const processAdtMessage = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/fhir/adt/process`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message_type: "A01",
          patient_id: `pt_${Date.now()}`,
          patient_name: "Demo Patient",
          mrn: `MRN${Date.now().toString().slice(-6)}`,
          sending_facility: "Demo Hospital",
          event_datetime: new Date().toISOString(),
          diagnosis: "I50.9 - Heart Failure",
          attending_physician: "Dr. Smith"
        })
      });
      const data = await res.json();
      toast.success(`ADT processed: ${data.action_taken}`);
      loadFhirData();
    } catch (err) {
      toast.error("Failed to process ADT message");
    }
    setLoading(false);
  };

  const createPrescription = async () => {
    if (!prescriptionForm.patientId || !prescriptionForm.patientName) {
      toast.error("Please enter patient ID and name");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/eprescribe/prescribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: prescriptionForm.patientId,
          patient_name: prescriptionForm.patientName,
          medication: prescriptionForm.medication.replace('_', ' '),
          strength: prescriptionForm.strength,
          form: "tablet",
          quantity: prescriptionForm.quantity,
          days_supply: prescriptionForm.daysSupply,
          sig: prescriptionForm.sig,
          refills: 0,
          prescriber: {
            npi: "1234567890",
            name: "Dr. Demo Prescriber",
            dea_number: "AB1234567",
            specialty: "Hospice & Palliative Medicine"
          },
          pharmacy_id: prescriptionForm.pharmacyId
        })
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`Prescription created: ${data.prescription_id}`);
        if (data.warnings?.length) {
          toast.warning(data.warnings.join(', '));
        }
      } else {
        toast.error(data.detail?.issues?.join(', ') || "Failed to create prescription");
      }
    } catch (err) {
      toast.error("Failed to create prescription");
    }
    setLoading(false);
  };

  const checkEligibility = async () => {
    if (!patientInsurance.memberId || !patientInsurance.patientName) {
      toast.error("Please enter member ID and patient name");
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/insurance/eligibility/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_insurance: {
            patient_id: `pt_${Date.now()}`,
            patient_name: patientInsurance.patientName,
            patient_dob: patientInsurance.patientDob || "1950-01-01",
            member_id: patientInsurance.memberId,
            payer_code: patientInsurance.payerCode
          },
          service_date: new Date().toISOString().split('T')[0],
          service_type: "hospice"
        })
      });
      const data = await res.json();
      setEligibilityResult(data);
      toast.success(data.eligible ? "Patient is eligible" : "Patient not eligible");
    } catch (err) {
      toast.error("Eligibility check failed");
    }
    setLoading(false);
  };

  const verifyBenefits = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/insurance/benefits/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_insurance: {
            patient_id: `pt_${Date.now()}`,
            patient_name: patientInsurance.patientName || "Demo Patient",
            patient_dob: patientInsurance.patientDob || "1950-01-01",
            member_id: patientInsurance.memberId || "DEMO123",
            payer_code: patientInsurance.payerCode
          },
          diagnosis_codes: ["I50.9", "R53.83"],
          requested_services: ["Nursing care", "Medical equipment", "Drugs for symptom control"]
        })
      });
      const data = await res.json();
      setBenefitResult(data);
      toast.success("Benefits verified");
    } catch (err) {
      toast.error("Benefit verification failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/hospice-ehr" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">External Integrations</h1>
              <p className="text-slate-600">FHIR, e-Prescribing, and insurance verification</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
              <Network className="w-3 h-3 mr-1" />
              Connected Systems
            </Badge>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-cyan-600">FHIR Connections</p>
                  <p className="text-2xl font-bold text-cyan-900">{fhirConnections.length}</p>
                </div>
                <Network className="w-8 h-8 text-cyan-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">ADT Messages</p>
                  <p className="text-2xl font-bold text-orange-900">{adtMessages.length}</p>
                </div>
                <FileText className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Formulary Meds</p>
                  <p className="text-2xl font-bold text-purple-900">{Object.keys(formulary).length}</p>
                </div>
                <Pill className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600">Payers</p>
                  <p className="text-2xl font-bold text-green-900">{Object.keys(payers).length}</p>
                </div>
                <Shield className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="fhir" className="flex items-center gap-2" data-testid="fhir-tab">
              <Network className="w-4 h-4" />
              FHIR/HL7
            </TabsTrigger>
            <TabsTrigger value="eprescribe" className="flex items-center gap-2" data-testid="eprescribe-tab">
              <Pill className="w-4 h-4" />
              E-Prescribing
            </TabsTrigger>
            <TabsTrigger value="insurance" className="flex items-center gap-2" data-testid="insurance-tab">
              <Shield className="w-4 h-4" />
              Insurance
            </TabsTrigger>
          </TabsList>

          {/* FHIR Tab */}
          <TabsContent value="fhir" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-cyan-600" />
                    FHIR Resources
                  </CardTitle>
                  <CardDescription>Supported FHIR R4 resources</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {supportedResources.map(resource => (
                      <Badge key={resource} variant="outline">{resource}</Badge>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t">
                    <Button 
                      onClick={processAdtMessage}
                      disabled={loading}
                      className="w-full"
                      data-testid="process-adt-btn"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {loading ? "Processing..." : "Simulate ADT A01 (Admit)"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent ADT Messages</CardTitle>
                </CardHeader>
                <CardContent>
                  {adtMessages.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {adtMessages.map((msg, idx) => (
                        <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <Badge variant="outline">{msg.message_type} - {msg.message_type_name}</Badge>
                            <span className="text-xs text-slate-500">{new Date(msg.received_at).toLocaleTimeString()}</span>
                          </div>
                          <p className="font-medium mt-1">{msg.patient_name}</p>
                          <p className="text-sm text-slate-600">MRN: {msg.mrn}</p>
                          <p className="text-xs text-slate-500 mt-1">From: {msg.sending_facility}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>No ADT messages yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* E-Prescribe Tab */}
          <TabsContent value="eprescribe" className="space-y-6">
            {/* AI E-Prescription Quick Access */}
            <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-violet-100 rounded-lg">
                      <Brain className="w-6 h-6 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-violet-900">AI-Powered E-Prescription</h3>
                      <p className="text-sm text-violet-600">Voice prescribing, AI recommendations, and comfort kits</p>
                    </div>
                  </div>
                  <Link to="/ai-eprescription">
                    <Button className="bg-violet-600 hover:bg-violet-700" data-testid="ai-rx-btn">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Open AI E-Prescription
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Pill className="w-5 h-5 text-purple-600" />
                    Create Prescription
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Patient ID</Label>
                      <Input
                        value={prescriptionForm.patientId}
                        onChange={(e) => setPrescriptionForm({...prescriptionForm, patientId: e.target.value})}
                        placeholder="Patient ID"
                      />
                    </div>
                    <div>
                      <Label>Patient Name</Label>
                      <Input
                        value={prescriptionForm.patientName}
                        onChange={(e) => setPrescriptionForm({...prescriptionForm, patientName: e.target.value})}
                        placeholder="Patient name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Medication</Label>
                    <Select value={prescriptionForm.medication} onValueChange={(v) => setPrescriptionForm({...prescriptionForm, medication: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(formulary).map(med => (
                          <SelectItem key={med} value={med}>
                            {med.replace(/_/g, ' ')} {formulary[med].schedule ? `(Schedule ${formulary[med].schedule})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label>Strength</Label>
                      <Input
                        value={prescriptionForm.strength}
                        onChange={(e) => setPrescriptionForm({...prescriptionForm, strength: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        value={prescriptionForm.quantity}
                        onChange={(e) => setPrescriptionForm({...prescriptionForm, quantity: parseInt(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Days Supply</Label>
                      <Input
                        type="number"
                        value={prescriptionForm.daysSupply}
                        onChange={(e) => setPrescriptionForm({...prescriptionForm, daysSupply: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Pharmacy</Label>
                    <Select value={prescriptionForm.pharmacyId} onValueChange={(v) => setPrescriptionForm({...prescriptionForm, pharmacyId: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {pharmacies.map(pharm => (
                          <SelectItem key={pharm.id} value={pharm.id}>
                            {pharm.name} ({pharm.type})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    onClick={createPrescription}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    data-testid="create-rx-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {loading ? "Creating..." : "Create Prescription"}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Hospice Formulary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {Object.entries(formulary).map(([name, med]) => (
                      <div key={name} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium capitalize">{name.replace(/_/g, ' ')}</span>
                          {med.schedule && (
                            <Badge variant={med.schedule === 'II' ? 'destructive' : 'outline'}>
                              Schedule {med.schedule}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          Forms: {med.forms?.join(', ')}
                        </p>
                        <p className="text-xs text-slate-500">
                          Strengths: {med.strengths?.join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Insurance Tab */}
          <TabsContent value="insurance" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    Eligibility Check
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Payer</Label>
                    <Select value={patientInsurance.payerCode} onValueChange={(v) => setPatientInsurance({...patientInsurance, payerCode: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(payers).map(([code, payer]) => (
                          <SelectItem key={code} value={code}>{payer.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Member ID</Label>
                    <Input
                      value={patientInsurance.memberId}
                      onChange={(e) => setPatientInsurance({...patientInsurance, memberId: e.target.value})}
                      placeholder="Member ID"
                    />
                  </div>

                  <div>
                    <Label>Patient Name</Label>
                    <Input
                      value={patientInsurance.patientName}
                      onChange={(e) => setPatientInsurance({...patientInsurance, patientName: e.target.value})}
                      placeholder="Patient name"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={checkEligibility}
                      disabled={loading}
                      className="flex-1"
                      data-testid="check-eligibility-btn"
                    >
                      Check Eligibility
                    </Button>
                    <Button 
                      onClick={verifyBenefits}
                      disabled={loading}
                      variant="outline"
                      className="flex-1"
                      data-testid="verify-benefits-btn"
                    >
                      Verify Benefits
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Eligibility Result</CardTitle>
                </CardHeader>
                <CardContent>
                  {eligibilityResult ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                        <span>Status</span>
                        <div className="flex items-center gap-2">
                          {eligibilityResult.eligible ? (
                            <><CheckCircle className="w-4 h-4 text-green-500" /><Badge className="bg-green-500">Eligible</Badge></>
                          ) : (
                            <><XCircle className="w-4 h-4 text-red-500" /><Badge variant="destructive">Not Eligible</Badge></>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Payer</span>
                          <span className="font-medium">{eligibilityResult.payer_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Member ID</span>
                          <span className="font-medium">{eligibilityResult.member_id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Coverage Status</span>
                          <span className="font-medium capitalize">{eligibilityResult.coverage_status}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Hospice Benefit</span>
                          <span className="font-medium">{eligibilityResult.hospice_benefit_available ? 'Available' : 'Not Available'}</span>
                        </div>
                      </div>

                      {eligibilityResult.rejection_reason && (
                        <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                          {eligibilityResult.rejection_reason}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      <Shield className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>Run eligibility check</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Benefits Detail</CardTitle>
                </CardHeader>
                <CardContent>
                  {benefitResult ? (
                    <div className="space-y-3">
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="font-medium text-green-700">Hospice Benefit</p>
                        <p className="text-sm text-green-600">{benefitResult.hospice_benefit?.benefit_period}</p>
                      </div>

                      {benefitResult.verified_services?.length > 0 && (
                        <div>
                          <p className="font-medium text-sm mb-2">Covered Services</p>
                          <div className="space-y-1">
                            {benefitResult.verified_services.map((svc, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                                <span>{svc.service}</span>
                                <div className="flex items-center gap-2">
                                  {svc.copay > 0 && <span className="text-xs text-slate-500">${svc.copay} copay</span>}
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-slate-600">Deductible</p>
                          <p className="font-medium">${benefitResult.deductible?.individual || 0}</p>
                          <Badge variant="outline" className="mt-1">
                            {benefitResult.deductible?.met ? 'Met' : 'Not Met'}
                          </Badge>
                        </div>
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-slate-600">Out-of-Pocket Max</p>
                          <p className="font-medium">${benefitResult.out_of_pocket_max?.amount || 0}</p>
                          <Badge variant="outline" className="mt-1">
                            {benefitResult.out_of_pocket_max?.met ? 'Met' : 'Not Met'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      <Building className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>Verify benefits to see details</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Payers Reference */}
            <Card>
              <CardHeader>
                <CardTitle>Supported Payers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(payers).map(([code, payer]) => (
                    <div key={code} className="p-3 border rounded-lg">
                      <p className="font-medium">{payer.name}</p>
                      <Badge variant="outline" className="mt-1">{payer.type}</Badge>
                      <p className="text-xs text-slate-500 mt-2">{payer.typical_benefit_period}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
